import type {
  ExistingTreeRepairCategory,
  ExistingTreeRepairPlan,
  ExistingTreeRequiredApproval,
} from './ExistingTreeRepairPlan';
import type {
  ExistingTreeAuditSeverity,
} from './ExistingTreeStructuralAudit';

export interface ExistingTreeRepairPlanSummary {
  totalItems: number;
  blockingItems: number;
  warningItems: number;
  informationalItems: number;
  categoryCounts: Record<
    ExistingTreeRepairCategory,
    number
  >;
  approvalCounts: Record<
    ExistingTreeRequiredApproval,
    number
  >;
  executionAllowed: false;
  productionWritesAllowed: false;
}

const EMPTY_CATEGORY_COUNTS: Record<
  ExistingTreeRepairCategory,
  number
> = {
  REFERENCE_INTEGRITY: 0,
  PARENT_ROLE_REVIEW: 0,
  IDENTITY_REVIEW: 0,
  GENERATION_REVIEW: 0,
  DISPLAY_PREFERENCE: 0,
};

const EMPTY_APPROVAL_COUNTS: Record<
  ExistingTreeRequiredApproval,
  number
> = {
  TREE_OWNER: 0,
  CLAIMED_MEMBER: 0,
  IDENTITY_REVIEW: 0,
  GOVERNED_OPERATOR_REVIEW: 0,
};

const countSeverity = (
  plan: ExistingTreeRepairPlan,
  severity: ExistingTreeAuditSeverity,
): number =>
  plan.items.filter(
    (item) => item.severity === severity,
  ).length;

export function summarizeExistingTreeRepairPlan(
  plan: ExistingTreeRepairPlan,
): ExistingTreeRepairPlanSummary {
  const categoryCounts = {
    ...EMPTY_CATEGORY_COUNTS,
  };

  const approvalCounts = {
    ...EMPTY_APPROVAL_COUNTS,
  };

  for (const item of plan.items) {
    categoryCounts[item.category] += 1;

    for (const approval of item.requiredApprovals) {
      approvalCounts[approval] += 1;
    }
  }

  return {
    totalItems: plan.itemCount,
    blockingItems: countSeverity(
      plan,
      'blocking',
    ),
    warningItems: countSeverity(
      plan,
      'warning',
    ),
    informationalItems: countSeverity(
      plan,
      'info',
    ),
    categoryCounts,
    approvalCounts,
    executionAllowed: false,
    productionWritesAllowed: false,
  };
}
